import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import logoImg from '../assets/logo.png';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'verifying' | 'success' | 'already_used' | 'error'>('verifying');
  const [message, setMessage] = useState('Verifying your email...');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('No verification token provided.');
      return;
    }

    fetch('/auth/verify-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (res.ok) {
          setStatus('success');
          setMessage(data.message || 'Email verified successfully!');
        } else if (data.error?.code === 'INVALID_TOKEN') {
          // Token already used — email was already verified
          setStatus('already_used');
          setMessage(data.error?.message || 'This link has already been used.');
        } else {
          setStatus('error');
          setMessage(data.error?.message || 'Verification failed.');
        }
      })
      .catch(() => {
        setStatus('error');
        setMessage('Something went wrong. Please try again later.');
      });
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-6 p-8 bg-white rounded-lg shadow text-center">
        <img src={logoImg} alt="Unified LLM Platform" className="w-20 h-20 mx-auto" />

        {status === 'verifying' && (
          <div className="text-gray-600">
            <div className="animate-spin inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mb-4" />
            <p>{message}</p>
          </div>
        )}

        {status === 'success' && (
          <div>
            <div className="text-green-500 text-5xl mb-4">{'\u2713'}</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Email Verified</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <Link
              to="/login"
              className="inline-block px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
            >
              Sign In
            </Link>
          </div>
        )}

        {status === 'already_used' && (
          <div>
            <div className="text-blue-500 text-5xl mb-4">{'\u2713'}</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Already Verified</h2>
            <p className="text-gray-600 mb-6">Your email has already been verified. You can sign in to your account.</p>
            <Link
              to="/login"
              className="inline-block px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
            >
              Sign In
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div>
            <div className="text-red-500 text-5xl mb-4">{'\u2717'}</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Verification Failed</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <Link
              to="/login"
              className="inline-block px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
            >
              Back to Sign In
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
